import reportjoborderService from '../../../services/reportjoborderService';
import { loadDeferredTotal } from '../../../utils/deferredTotal';
import filterService from '../../../services/filterService';

import {
    setReportjoborderLoading,
    setReportjoborderError,
    setReportjoborderList,
    setReportjoborderCurrent,
    setReportjoborderPagination,
    setReportjoborderSort,
    setReportjoborderFilters,
    setReportjoborderDefaultFilter,
    setReportjoborderSettings,
    clearReportjoborderCurrent,
    clearReportjoborderError,
    setReportjoborderFilterLoading,
    changeReportjoborderFilterCondition,
    addReportjoborderFilterCondition,
    updateReportjoborderFilterConditionRule,
    updateReportjoborderFilterConditionValue,
    removeReportjoborderFilterCondition,
    setReportjoborderSelected,
    setReportjoborderDownloading,
    resetReportjoborderFiltersAndPagination,

    setReportjoborderClassicFilters
} from './reportjoborderSlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        // Only fetch settings if they haven't been initialized
        if (!getState().reportjoborder.isSettingsInitialized) {
            dispatch(setReportjoborderLoading(true));
            const response = await reportjoborderService.getSettings();

            // Set the settings
            dispatch(setReportjoborderSettings(response));

            const classicFilters = response?.classic_filters || [];
            dispatch(setReportjoborderClassicFilters(classicFilters));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setReportjoborderDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        console.error('Error fetching settings:', error);
        dispatch(setReportjoborderError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setReportjoborderLoading(false));
    }
};

export const fetchReportjoborders = (params = {}, filter = {}, shouldUseFilter, externalCondition = {}) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized

        const state = getState().reportjoborder;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : externalCondition;

        dispatch(setReportjoborderLoading(true));
        const response = await reportjoborderService.getList(finalQueryParams, finalActiveFilter, state.classicFilters);
        dispatch(setReportjoborderList(response));
        loadDeferredTotal(response, reportjoborderService, finalQueryParams, finalActiveFilter, state.classicFilters, dispatch, setReportjoborderPagination);
        return response;
    } catch (error) {
        console.error('Error in fetchReportjoborders:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setReportjoborderError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setReportjoborderLoading(false));
    }
};

export const fetchReportjoborder = (id) => async (dispatch) => {
    try {
        dispatch(setReportjoborderLoading(true));
        const response = await reportjoborderService.getOne(id);
        dispatch(setReportjoborderCurrent(response.data));
    } catch (error) {
        dispatch(setReportjoborderError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setReportjoborderLoading(false));
    }
};

export const createReportjoborder = (data) => async (dispatch) => {
    try {
        const response = await reportjoborderService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updateReportjoborder = (id, data) => async (dispatch) => {
    try {
        const response = await reportjoborderService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deleteReportjoborder = (id) => async (dispatch) => {
    try {
        dispatch(setReportjoborderLoading(true));
        const response = await reportjoborderService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setReportjoborderError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setReportjoborderLoading(false));
    }
};

export const downloadReportjoborder = (type) => async (dispatch, getState) => {
    try {
        const state = getState().reportjoborder;

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            export_type: type
        };

        const filter = state.defaultFilter;
        dispatch(setReportjoborderDownloading(true));
        const response = await reportjoborderService.downloadAsync(finalQueryParams, filter, state.classicFilters);
        dispatch(setReportjoborderDownloading(false));

        return response;
    } catch (error) {
        console.error('Error downloading reportjoborders:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setReportjoborderError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setReportjoborderDownloading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setReportjoborderPagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setReportjoborderSort(sort));
};

export const searchReportjoborders = (query) => (dispatch, getState) => {
    const currentFilters = getState().reportjoborder.filters;
    dispatch(setReportjoborderFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setReportjoborderDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changeReportjoborderFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addReportjoborderFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updateReportjoborderFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updateReportjoborderFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removeReportjoborderFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setReportjoborderFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().reportjoborder.settings;
            dispatch(setReportjoborderSettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setReportjoborderFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setReportjoborderFilterLoading(false));
        dispatch(setReportjoborderError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const setSelected = (selectedRows) => {
    return (dispatch) => {
        dispatch(setReportjoborderSelected(selectedRows));
    };
};

export const clearCurrentReportjoborder = () => {
    return (dispatch) => {
        dispatch(clearReportjoborderCurrent());
    };
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetReportjoborderFiltersAndPagination());
};

export const changeClassicFilter = (name, value) => (dispatch, getState) => {
    const currentClassicFilters = getState().reportjoborder.classicFilters;

    // Create a deep copy of the classic filters
    const updatedClassicFilters = { ...currentClassicFilters };
    
    // Update the nested value in the query object
    if (updatedClassicFilters[name]) {
        updatedClassicFilters[name] = {
            ...updatedClassicFilters[name],
            query: {
                ...updatedClassicFilters[name].query,
                ...(typeof value === 'object' && value !== null ? value : { value })
            }
        };
    }

    dispatch(setReportjoborderClassicFilters(updatedClassicFilters));
};